import React from 'react';
import {useAddDriverLicenceMutation, useLinkRecordToProfileMutation} from '../store/api/driverLicenceApi';
import {messageType, showMessages} from '../helpers/message';
import {BaseError} from '../types/apiTypes';

interface AddDriverLicenceAndLinkProfile {
    profileID?: number;
}

/**
 * Handles linking a profile to a driver licence record after it's been stored.
 */
const useAddDriverLicenceAndLinkProfile = ({profileID}: AddDriverLicenceAndLinkProfile) => {
    const [addDriverLicence, {
        isLoading: addLoading,
        isSuccess: addSuccess,
        isError: isAddError,
        error: addError,
        data: addData,
        reset: addReset
    }] = useAddDriverLicenceMutation();

    const [linkRecordToProfile, {
        isLoading: linkLoading,
        isSuccess: linkSuccess,
        isError: isLinkError,
        error: linkError,
        data: linkData,
        reset: linkReset
    }] = useLinkRecordToProfileMutation();

    React.useEffect(() => {
        if (addSuccess && addData && profileID) {
            // Link profile when DL is successfully stored.
            linkRecordToProfile({
                record_id: addData.id,
                profile_id: profileID,
            });
            addReset();
        } else if (isAddError) {
            const errorMessage = (addError as BaseError)?.data?.message;
            showMessages(errorMessage ?? "Error saving driver's licence.", messageType.error);
            addReset();
        } else if (linkSuccess) {
            showMessages("Driver's licence successfully added.", messageType.success);
            // Sometime warnings return when a new DL is added and linked.
            linkData?.warnings?.forEach(warning => {
                showMessages(warning, messageType.warn);
            });
            linkReset();
        } else if (isLinkError) {
            const errorMessage = (linkError as BaseError)?.data?.message;
            showMessages(errorMessage ?? "Error linking driver's licence to profile.", messageType.error);
            linkReset();
        }
    }, [addData, isAddError, addError, addReset, addSuccess, linkSuccess, isLinkError, linkRecordToProfile, profileID, linkReset, linkError, linkData?.warnings]);

    return {
        isLoading: addLoading || linkLoading,
        addDriverLicence
    }
}

export {useAddDriverLicenceAndLinkProfile};
